import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { CostGroupYearRate } from '../services/costGroupService';
import { costGroupService } from '../services/costGroupService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { CostGroupYearRateDrawer } from './CostGroupYearRateDrawer.tsx';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

function getYearRateGroupId(rate: CostGroupYearRate, fallbackGroupId: string | null): string {
  if ((rate as { group_id?: number }).group_id != null) {
    return String((rate as { group_id?: number }).group_id);
  }
  return rate.cost_group_id ?? fallbackGroupId ?? '';
}

export const CostGroupYearRatesSection: React.FC = () => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(currentYear);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedYearRate, setSelectedYearRate] = useState<CostGroupYearRate | null>(null);
  const [editingYearRate, setEditingYearRate] = useState<CostGroupYearRate | null>(null);

  const { data: costGroups = [] } = useQuery({
    queryKey: ['cost-groups'],
    queryFn: () => costGroupService.getCostGroups(),
  });

  useEffect(() => {
    if (costGroups.length > 0 && (selectedGroupId === null || selectedGroupId === '')) {
      setSelectedGroupId(costGroups[0].id);
    }
  }, [costGroups, selectedGroupId]);

  const yearRatesQueries = useQueries({
    queries: costGroups.map((g) => ({
      queryKey: ['cost-group-year-rates', g.id],
      queryFn: () => costGroupService.getYearRatesByCostGroupId(g.id),
    })),
  });
  const loadingRates = yearRatesQueries.some((q) => q.isLoading);
  const allYearRates = useMemo(() => {
    return yearRatesQueries.flatMap((q) => q.data ?? []);
  }, [yearRatesQueries]);
  const yearRates = useMemo(() => {
    if (selectedYear == null) return [];
    return allYearRates.filter((r) => r.calendar_year === selectedYear);
  }, [allYearRates, selectedYear]);

  const createMutation = useMutation({
    mutationFn: ({
      groupId,
      payload,
    }: {
      groupId: string;
      payload: { calendar_year: number; cost_rate: number; sales_rate: number };
    }) => costGroupService.createYearRate(groupId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-group-year-rates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      groupId,
      yid,
      payload,
    }: {
      groupId: string;
      yid: string;
      payload: { calendar_year: number; cost_rate: number; sales_rate: number };
    }) => costGroupService.updateYearRate(groupId, yid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-group-year-rates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ groupId, yid }: { groupId: string; yid: string }) =>
      costGroupService.deleteYearRate(groupId, yid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-group-year-rates'] });
    },
  });

  const handleRowClicked = useCallback((event: RowClickedEvent<CostGroupYearRate>) => {
    if (!event.data) return;
    setSelectedYearRate(event.data);
    setPanelMode('selected');
    setEditingYearRate(null);
  }, []);

  const handleFormClose = () => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingYearRate(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingYearRate(null);
  };

  const handleViewClose = () => {
    setPanelMode('empty');
    setSelectedYearRate(null);
    setEditingYearRate(null);
  };

  const handleDelete = (rate: CostGroupYearRate) => {
    const groupId = getYearRateGroupId(rate, selectedGroupId);
    if (groupId && window.confirm('Delete this year rate?')) {
      deleteMutation.mutate({ groupId, yid: rate.id });
      setSelectedYearRate(null);
      setPanelMode('empty');
    }
  };

  const columnDefs: ColDef<CostGroupYearRate>[] = useMemo(
    () => [
      {
        headerName: 'Cost group',
        field: 'cost_group',
        flex: 1,
        minWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
      {
        headerName: 'Cost rate',
        field: 'cost_rate',
        flex: 1,
        minWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (p: { value: number }) => (p.value != null ? String(p.value) : ''),
      },
      {
        headerName: 'Sales rate',
        field: 'sales_rate',
        flex: 1,
        minWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (p: { value: number }) => (p.value != null ? String(p.value) : ''),
      },
    ],
    []
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      theme: agGridTheme,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'edit' || panelMode === 'add' ? (
          costGroups.length > 0 || editingYearRate ? (
            <CostGroupYearRateDrawer
              isOpen={true}
              onClose={handleFormClose}
              yearRate={editingYearRate}
              costGroups={costGroups}
              selectedYear={selectedYear}
              onSave={async (payload, id, groupId) => {
                if (id) {
                  const gid = editingYearRate
                    ? getYearRateGroupId(editingYearRate, selectedGroupId)
                    : (selectedGroupId ?? '');
                  await updateMutation.mutateAsync({
                    groupId: gid,
                    yid: id,
                    payload,
                  });
                } else {
                  await createMutation.mutateAsync({ groupId: groupId!, payload });
                }
                justSavedRef.current = true;
                setPanelMode('empty');
                setSelectedYearRate(null);
              }}
            />
          ) : null
        ) : panelMode === 'selected' && selectedYearRate ? (
          <CostGroupYearRateDrawer
            key={selectedYearRate.id}
            isOpen={true}
            readOnly
            yearRate={selectedYearRate}
            onClose={handleViewClose}
            onSave={() => undefined}
            onEditRequest={() => {
              setEditingYearRate(selectedYearRate);
              setPanelMode('edit');
            }}
            onDeleteRequest={() => handleDelete(selectedYearRate)}
          />
        ) : null
      }
      list={
        <>
          <style>{agGridThemeStyles}</style>
          <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
            <div className="flex items-center">
              <label className="mr-2 text-sm text-text-secondary">Year:</label>
              <select
                className="mr-2 min-w-[120px] rounded-md border border-border-default bg-white p-2 text-sm text-text-primary"
                value={selectedYear ?? ''}
                onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Select year</option>
                {Array.from({ length: 11 }, (_, i) => currentYear - 5 + i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            {costGroups.length > 0 && (
              <Button
                type="button"
                onClick={() => {
                  setEditingYearRate(null);
                  setPanelMode('add');
                }}
              >
                <PlusIcon data-icon="inline-start" />
                Add year rate
              </Button>
            )}
          </div>
          {selectedYear != null &&
            (loadingRates ? (
              <p className="text-text-secondary">Loading...</p>
            ) : (
              <div
                className="ag-theme-material min-h-0 flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={yearRates}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  animateRows
                  domLayout="normal"
                  rowNumbers={true}
                  getRowId={(params) => params.data.id}
                />
              </div>
            ))}
        </>
      }
    />
  );
};
